import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UserPlus, Trash, ArrowRight, Eye, EyeSlash, Copy } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

function scoreBadge(p) {
  if (p == null) return "text-muted-foreground bg-muted border-border";
  if (p >= 70) return "text-[#059669] bg-[#059669]/10 border-[#059669]/20";
  if (p >= 50) return "text-accent bg-accent/10 border-accent/20";
  return "text-destructive bg-destructive/10 border-destructive/20";
}

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/admin/users").then((res) => setUsers(res.data));

  useEffect(() => {
    load();
  }, []);

  const genPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let p = "";
    for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setForm((f) => ({ ...f, password: p }));
    setShowPw(true);
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`Account created for ${form.name}`);
      navigator.clipboard?.writeText(`Email: ${form.email}\nPassword: ${form.password}`).catch(() => {});
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      setShowPw(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    const t = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/admin/users/${t.id}`);
      toast.success(`Removed ${t.name}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  if (!users) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 lg:py-14">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Accounts</p>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-foreground mt-1">
            Staff Accounts
          </h1>
          <p className="text-muted-foreground mt-2">Create login accounts and share the credentials with each staff member.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-user-button"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-primary text-white font-semibold hover:-translate-y-0.5 transition-transform duration-200"
            >
              <UserPlus size={18} weight="duotone" /> New account
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Create a staff account</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4 mt-2" data-testid="create-user-form">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Full name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="new-user-name"
                  className="w-full h-11 px-4 rounded-lg border border-input bg-white outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="new-user-email"
                  className="w-full h-11 px-4 rounded-lg border border-input bg-white outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors duration-200"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-foreground">Password</label>
                  <button type="button" onClick={genPassword} className="text-xs text-primary hover:text-accent font-medium transition-colors duration-200">
                    Generate
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    minLength={6}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="new-user-password"
                    className="w-full h-11 px-4 pr-12 rounded-lg border border-input bg-white outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors duration-200"
                  >
                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                  <Copy size={13} /> Credentials are copied to your clipboard when you create the account.
                </p>
              </div>
              <DialogFooter className="pt-2">
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="submit-create-user"
                  className="w-full h-11 rounded-full bg-primary text-white font-semibold hover:bg-primary/90 transition-colors duration-200 disabled:opacity-60"
                >
                  {busy ? "Creating..." : "Create account"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl bg-white border border-border shadow-sm mt-8 overflow-hidden">
        {users.length === 0 ? (
          <div className="p-12 text-center">
            <UserPlus size={40} weight="duotone" className="mx-auto text-muted-foreground" />
            <p className="text-foreground font-medium mt-4">No staff accounts yet.</p>
            <p className="text-muted-foreground text-sm mt-1">Create your first account to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Attempts</th>
                  <th className="px-6 py-3 font-medium">Best score</th>
                  <th className="px-6 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id} className="border-b border-border/60 hover:bg-muted/40 transition-colors duration-150" data-testid={`user-row-${i}`}>
                    <td className="px-6 py-3 font-medium text-foreground">{u.name}</td>
                    <td className="px-6 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-6 py-3 text-foreground">{u.attempts}</td>
                    <td className="px-6 py-3">
                      <span className={`font-semibold rounded-full border px-3 py-0.5 ${scoreBadge(u.best_score)}`}>
                        {u.best_score == null ? "\u2014" : `${u.best_score}%`}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-3">
                        <Link
                          to={`/admin/users/${u.id}`}
                          data-testid={`view-user-${i}`}
                          className="text-primary hover:text-accent inline-flex items-center gap-1 font-medium transition-colors duration-200"
                        >
                          Results <ArrowRight size={14} weight="bold" />
                        </Link>
                        <button
                          onClick={() => setDeleteTarget(u)}
                          data-testid={`delete-user-${i}`}
                          className="text-muted-foreground hover:text-destructive transition-colors duration-200"
                        >
                          <Trash size={18} weight="duotone" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the account and all of their test results. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-user" className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
